import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { cn } from '@crossnokaye/ux-common';

import type { DropdownOption } from '@repo/sdk/models/components';
import { ButtonComponentActionConfigVariant } from '@repo/sdk/models/components';

import { DropdownCell } from '@/components/data-table';
import { mapLegacyButtonVariantToClassName } from '@/features/equipment-browser/utils';

interface EquipmentBulkEditTableDropdownCellProps {
  columnId: string;
  disabled?: boolean;
  disableItems?: boolean;
  dropdownOptions?: DropdownOption[];
  tooltipContent?: string;
  rowId: string;
}

export const EquipmentBulkEditTableDropdownCell = ({
  columnId,
  disabled,
  disableItems,
  dropdownOptions = [],
  tooltipContent,
  rowId,
}: EquipmentBulkEditTableDropdownCellProps): ReactNode => {
  const { control } = useFormContext();
  const fieldName = `values.${rowId}.${columnId}`;
  const { field } = useController({
    control,
    name: fieldName,
  });

  // Adapt SDK options to the cell's option shape (value + label only).
  const options = useMemo(
    () => dropdownOptions.map((option) => ({ value: option.value, label: option.label })),
    [dropdownOptions]
  );

  return (
    <DropdownCell
      data-component="EquipmentBulkEditTableDropdownCell"
      // No text colour here on purpose: the `btn-tier2` variant pairs a foreground
      // with every background it sets, including the near-white hover background. A
      // `text-*` utility outranks those `@layer components` rules and would pin the
      // colour across all states, making the label invisible on hover (UX-4042).
      className={cn(
        mapLegacyButtonVariantToClassName(ButtonComponentActionConfigVariant.Tier2),
        'w-full text-sm [&>span:first-child]:inline-block [&>span:first-child]:overflow-hidden',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50'
      )}
      disabled={disabled}
      disableItems={disableItems}
      onSelect={(option) => field.onChange(option.value)}
      options={options}
      placeholder="Select value ..."
      tooltipContent={disabled || disableItems ? tooltipContent : undefined}
      value={(field.value as string) ?? undefined}
    />
  );
};
